import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Wine, LogIn, UserPlus, Mail, Lock, User, ArrowRight, Shield, Check, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const { joinCode } = useParams();
  const [activeTab, setActiveTab] = useState(joinCode ? "register" : "login");
  const [venueInfo, setVenueInfo] = useState(null);
  
  // Login form
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginGdpr, setLoginGdpr] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Register form
  const [registerName, setRegisterName] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [registerGdpr, setRegisterGdpr] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);

  // Resolve join code to venue info
  useEffect(() => {
    if (joinCode) {
      axios.get(`${API}/org/join/${joinCode}`)
        .then(res => setVenueInfo(res.data))
        .catch(() => setVenueInfo(null));
    }
  }, [joinCode]);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    sessionStorage.setItem('gdpr_consent', 'true');
    if (joinCode) sessionStorage.setItem('join_code', joinCode);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) { toast.error("Please fill in all fields"); return; }
    if (!loginGdpr) { toast.error("Please accept the privacy policy to continue"); return; }

    setLoginLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: loginEmail, password: loginPassword, gdpr_consent: loginGdpr
      }, { withCredentials: true });
      
      if (response.data.session_token) {
        localStorage.setItem("session_token", response.data.session_token);
      }
      
      toast.success("Login successful!");
      const user = response.data.user;
      navigate("/dashboard", { state: { user } });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerName || !registerEmail || !registerPassword) { toast.error("Please fill in all fields"); return; }
    if (!registerGdpr) { toast.error("Please accept the privacy policy to continue"); return; }
    if (registerPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }

    setRegisterLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        name: registerName, email: registerEmail, password: registerPassword,
        gdpr_consent: registerGdpr, join_code: joinCode || null
      }, { withCredentials: true });
      
      if (response.data.session_token) {
        localStorage.setItem("session_token", response.data.session_token);
      }

      toast.success("Account created!");
      const user = response.data.user;
      navigate("/dashboard", { state: { user } });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setRegisterLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col">
      <header className="p-6">
        <div className="flex items-center gap-2">
          <Wine className="text-[hsl(var(--primary))]" size={32} />
          <span className="text-2xl font-bold tracking-tight text-[hsl(var(--primary))]">FlashSpecs</span>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Venue join banner */}
          {venueInfo && (
            <div className="mb-4 p-4 rounded-lg border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5" data-testid="venue-join-banner">
              <div className="flex items-center gap-3">
                <Building2 size={20} className="text-[hsl(var(--primary))] shrink-0" />
                <div>
                  <p className="text-sm font-bold">Join {venueInfo.org_name}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Venue: {venueInfo.venue_name}</p>
                </div>
              </div>
            </div>
          )}

          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl">
                {joinCode ? "Join Your Team" : "Welcome to FlashSpecs"}
              </CardTitle>
              <p className="text-[hsl(var(--muted-foreground))] text-sm mt-2">
                {joinCode ? "Create an account to get started" : "The cocktail knowledge testing platform"}
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Google Login Button */}
              <Button
                onClick={handleGoogleLogin}
                variant="outline"
                className="w-full h-12 flex items-center justify-center gap-3 text-base"
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[hsl(var(--border))]"></div></div>
                <div className="relative flex justify-center text-xs uppercase"><span className="bg-[hsl(var(--card))] px-2 text-[hsl(var(--muted-foreground))]">Or</span></div>
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2 bg-[hsl(var(--secondary))]">
                  <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
                </TabsList>

                {/* Login Tab */}
                <TabsContent value="login">
                  <form onSubmit={handleEmailLogin} className="space-y-4 pt-2">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Name or Email</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" size={18} />
                        <Input id="login-email" type="text" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)}
                          placeholder="Your name or email" className="pl-10 bg-[hsl(var(--secondary))] border-[hsl(var(--border))]" data-testid="login-email" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" size={18} />
                        <Input id="login-password" type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)}
                          placeholder="Your password" className="pl-10 bg-[hsl(var(--secondary))] border-[hsl(var(--border))]" data-testid="login-password" />
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Checkbox id="login-gdpr" checked={loginGdpr} onCheckedChange={setLoginGdpr} data-testid="login-gdpr" />
                      <label htmlFor="login-gdpr" className="text-xs text-[hsl(var(--muted-foreground))] leading-tight cursor-pointer">
                        I agree to the processing of my personal data in accordance with the{" "}
                        <a href="#privacy" className="text-[hsl(var(--primary))] hover:underline">Privacy Policy</a>.
                      </label>
                    </div>
                    <Button type="submit" disabled={loginLoading} className="w-full btn-glow bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90" data-testid="login-submit">
                      {loginLoading ? "Signing in..." : "Sign In"} <ArrowRight size={18} className="ml-2" />
                    </Button>
                  </form>
                </TabsContent>

                {/* Register Tab */}
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4 pt-2">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" size={18} />
                        <Input id="register-name" value={registerName} onChange={(e) => setRegisterName(e.target.value)}
                          placeholder="Your full name" className="pl-10 bg-[hsl(var(--secondary))] border-[hsl(var(--border))]" data-testid="register-name" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" size={18} />
                        <Input id="register-email" type="email" value={registerEmail} onChange={(e) => setRegisterEmail(e.target.value)}
                          placeholder="your@email.com" className="pl-10 bg-[hsl(var(--secondary))] border-[hsl(var(--border))]" data-testid="register-email" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" size={18} />
                        <Input id="register-password" type="password" value={registerPassword} onChange={(e) => setRegisterPassword(e.target.value)}
                          placeholder="Min 6 characters" className="pl-10 bg-[hsl(var(--secondary))] border-[hsl(var(--border))]" data-testid="register-password" />
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Checkbox id="register-gdpr" checked={registerGdpr} onCheckedChange={setRegisterGdpr} data-testid="register-gdpr" />
                      <label htmlFor="register-gdpr" className="text-xs text-[hsl(var(--muted-foreground))] leading-tight cursor-pointer">
                        I agree to the processing of my personal data in accordance with the{" "}
                        <a href="#privacy" className="text-[hsl(var(--primary))] hover:underline">Privacy Policy</a>.
                      </label>
                    </div>
                    <Button type="submit" disabled={registerLoading} className="w-full btn-glow bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90" data-testid="register-submit">
                      {registerLoading ? "Creating account..." : "Create Account"} <UserPlus size={18} className="ml-2" />
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <div className="mt-6 p-4 rounded-lg bg-[hsl(var(--card))] border border-[hsl(var(--border))]">
            <div className="flex items-start gap-3">
              <Shield className="text-[hsl(var(--primary))] mt-0.5 flex-shrink-0" size={20} />
              <div>
                <h4 className="font-medium text-sm mb-1">Your Privacy Matters</h4>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  We collect and process your data only for providing our service. Your information is stored securely and never shared with third parties.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="p-6 text-center text-xs text-[hsl(var(--muted-foreground))]">
        <p>&copy; 2026 FlashSpecs. All rights reserved.</p>
      </footer>
    </div>
  );
}
